class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector(".cart-recommendations__list");
    const cfg = window.__cartRecConfig || {};
    this._limit    = cfg.limit || 4;
    this._fallback = cfg.manualCollection || "";
    this._text = {
      addToCart:     cfg.addToCartText     || "Add to cart",
      selectOptions: cfg.selectOptionsText || "Select options",
      soldOut:       cfg.soldOutText       || "Sold out",
      addedToCart:   cfg.addedToCartText   || "Added to cart!",
    };

    const headingEl = this.querySelector(".cart-recommendations__title");
    if (headingEl && cfg.heading) headingEl.textContent = cfg.heading;

    if (cfg.showInDrawer === false) {
      this.closest(".cart-recommendations-wrapper").hidden = true;
      return;
    }

    this._cartIds = new Set(
      (this.dataset.cartItemIds || "").split(",").filter(Boolean).map(Number)
    );
    this._onCartUpdated = this._handleCartUpdated.bind(this);
    this._onAjaxAdded   = this._handleAjaxAdded.bind(this);
    document.addEventListener("cart:updated",      this._onCartUpdated);
    document.addEventListener("ajaxProduct:added", this._onAjaxAdded);

    if (this.dataset.productId || this._fallback) {
      this._loadRecommendations(this.dataset.productId);
    }
  }

  disconnectedCallback() {
    document.removeEventListener("cart:updated",      this._onCartUpdated);
    document.removeEventListener("ajaxProduct:added", this._onAjaxAdded);
  }

  _handleAjaxAdded(e) {
    const productId = e.detail && e.detail.product && e.detail.product.product_id;
    if (!productId) return;
    this.dataset.productId = String(productId);
    this.dataset.recUrl = `/recommendations/products?section_id=cart-recommendations&product_id=${productId}&limit=10`;
    this._cartIds = new Set([productId]);
    this._loadRecommendations(String(productId));
  }

  _handleCartUpdated(e) {
    const items = e.detail && e.detail.cart && e.detail.cart.items;
    if (!items) return;
    this._cartIds = new Set(items.map((i) => i.product_id));
    const wrapper = this.closest(".cart-recommendations-wrapper");
    if (!items.length) { if (wrapper) wrapper.hidden = true; return; }
    const newId = String(items[0].product_id);
    this.dataset.productId = newId;
    this._loadRecommendations(newId);
  }

  _loadRecommendations(productId) {
    if (!productId && !this._fallback) return;
    if (this._fallback) { this._loadCollectionFallback(this._fallback, this._limit); return; }

    fetch(this.dataset.recUrl.replace(/product_id=[^&]*/, "product_id=" + productId))
      .then((r) => r.text())
      .then((html) => {
        const doc   = new DOMParser().parseFromString(html, "text/html");
        const cards = [...doc.querySelectorAll(".cart-recommendations-card")]
          .filter((c) => !this._cartIds.has(Number(c.dataset.productId)))
          .slice(0, this._limit);
        cards.length ? this._renderCards(cards) : this._setVisible(false);
      })
      .catch(() => this._setVisible(false));
  }

  _loadCollectionFallback(handle, limit) {
    fetch(`/collections/${handle}/products.json?limit=${limit * 2}`)
      .then((r) => r.json())
      .then(({ products = [] }) => {
        const cards = products
          .filter((p) => p.variants && p.variants.some((v) => v.available) && !this._cartIds.has(p.id))
          .slice(0, limit)
          .map((p) => this._buildCollectionCard(p))
          .filter(Boolean);
        cards.length ? this._renderCards(cards) : this._setVisible(false);
      })
      .catch(() => this._setVisible(false));
  }

  _renderCards(cards) {
    this.list.innerHTML = "";
    cards.forEach((c) => this.list.appendChild(c));
    this._setVisible(true);
    this._bindButtons();
    if (window.SimpleBar) {
      if (this._simpleBar) this._simpleBar.unMount();
      this._simpleBar = new SimpleBar(this.list, { autoHide: false });
    }
  }

  _setVisible(show) {
    const w = this.closest(".cart-recommendations-wrapper");
    if (w) w.hidden = !show;
  }

  _buildCollectionCard(p) {
    const variant = p.variants.find((v) => v.available) || p.variants[0];
    if (!variant) return null;

    const isMulti  = p.variants.length > 1;
    const price    = this._toCents(variant.price);
    const compare  = this._toCents(variant.compare_at_price);
    const onSale   = compare > price;

    const rawImg = p.featured_image || (p.images && p.images[0]) || null;
    const imgUrl = rawImg
      ? (typeof rawImg === "object" ? rawImg.src : String(rawImg))
          .replace(/(\.(jpg|jpeg|png|gif|webp))(\?.*)?$/i, "_160x$1$3")
      : null;

    const variantData = JSON.stringify({
      title:          p.title,
      url:            `/products/${p.handle}`,
      featured_image: imgUrl || "",
      options:        (p.options || ["Title"]).map((o) => (typeof o === "object" ? o.name : o)),
      variants:       p.variants.map((v) => ({
        id: v.id, title: v.title, available: v.available,
        price: this._toCents(v.price), compare_at_price: this._toCents(v.compare_at_price),
        option1: v.option1, option2: v.option2, option3: v.option3,
      })),
    });

    const svg = `<svg fill="#000000" viewBox="0 0 24 24" data-name="Flat Line" xmlns="http://www.w3.org/2000/svg" class="icon flat-line"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"><line id="primary-upstroke" x1="10.95" y1="20.5" x2="11.05" y2="20.5" style="fill: none; stroke: #000000; stroke-linecap: round; stroke-linejoin: round; stroke-width: 2.5;"></line><line id="primary-upstroke-2" data-name="primary-upstroke" x1="16.95" y1="20.5" x2="17.05" y2="20.5" style="fill: none; stroke: #000000; stroke-linecap: round; stroke-linejoin: round; stroke-width: 2.5;"></line><path id="primary" d="M14,5v6m3-3H11" style="fill: none; stroke: #000000; stroke-linecap: round; stroke-linejoin: round; stroke-width: 2;"></path><path id="primary-2" data-name="primary" d="M3,3H5.2a1,1,0,0,1,1,.78L8.82,15.22a1,1,0,0,0,1,.78h8.42a1,1,0,0,0,1-.76L21,8" style="fill: none; stroke: #000000; stroke-linecap: round; stroke-linejoin: round; stroke-width: 2;"></path></g></svg>`;

    let action;
    if (!variant.available) {
      action = `<span class="btn btn--small btn--secondary cart-recommendations-sold-out" aria-disabled="true">${this._text.soldOut}</span>`;
    } else if (isMulti) {
      action = `<button type="button" class="cart-recommendations-select-btn" aria-label="${this._esc(this._text.selectOptions)}">${svg}</button>`;
    } else {
      action = `<button type="button" class="cart-recommendations-atc-btn" data-variant-id="${variant.id}" data-single-variant="true" aria-label="${this._esc(this._text.addToCart)}">${svg}</button>`;
    }

    const wrap = document.createElement("div");
    wrap.className       = "cart-recommendations-card";
    wrap.dataset.productId = p.id;
    wrap.innerHTML = `
      <a href="/products/${p.handle}" class="cart-recommendations-card__image-link" aria-label="${this._esc(p.title)}">
        ${imgUrl
          ? `<img src="${imgUrl}" alt="${this._esc(p.title)}" width="80" height="80" loading="lazy" class="cart-recommendations-card__image">`
          : `<div class="cart-recommendations-card__image-placeholder"></div>`}
      </a>
      <div class="cart-recommendations-card__info">
        <a href="/products/${p.handle}" class="cart-recommendations-card__title">${p.title}</a>
        <div class="cart-recommendations-card__price">
          ${onSale ? `<s class="cart-recommendations-card__price--compare">${this._money(compare)}</s>` : ""}
          <span class="cart-recommendations-card__price--current${onSale ? " cart-recommendations-card__price--sale" : ""}">${this._money(price)}</span>
        </div>
      </div>
      <div class="cart-recommendations-card__action">${action}</div>`;

    if (isMulti && variant.available) {
      const s = document.createElement("script");
      s.type = "application/json";
      s.className   = "cart-recommendations-card__variants-data";
      s.textContent = variantData;
      wrap.appendChild(s);
    }
    return wrap;
  }

  _bindButtons() {
    this.querySelectorAll(".cart-recommendations-atc-btn[data-single-variant]").forEach((btn) =>
      btn.addEventListener("click", (e) => { e.stopPropagation(); this._addToCart(btn.dataset.variantId, btn, null); })
    );
    this.querySelectorAll(".cart-recommendations-select-btn").forEach((btn) =>
      btn.addEventListener("click", (e) => { e.stopPropagation(); this._openVariantModal(btn); })
    );
  }

  _addToCart(variantId, btn, modal) {
    const orig = btn.innerHTML;
    btn.disabled = true;
    btn.innerHTML = "&bull;&bull;&bull;";

    fetch("/cart/add.js", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ id: Number(variantId), quantity: 1, properties: { _added_from_recommendations: "true" } }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status && data.status !== 200) { btn.disabled = false; btn.innerHTML = orig; return; }

        if (modal) {
          this._showModalSuccess(modal);
        } else {
          btn.innerHTML = "&#10003;";
          setTimeout(() => { btn.disabled = false; btn.innerHTML = orig; }, 2000);
        }

        fetch("/cart.js")
          .then((r) => r.json())
          .then((cart) => {
            this._cartIds = new Set(cart.items.map((i) => i.product_id));
            document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart } }));
            document.dispatchEvent(new CustomEvent("cart:build"));
            if (!modal) {
              setTimeout(() => {
                this._loadRecommendations(String((cart.items[0] || {}).product_id || this.dataset.productId));
              }, 400);
            }
          });
      })
      .catch(() => { btn.disabled = false; btn.innerHTML = orig; });
  }

  _openVariantModal(btn) {
    const scriptEl = btn.closest(".cart-recommendations-card")
      && btn.closest(".cart-recommendations-card").querySelector(".cart-recommendations-card__variants-data");
    let data;
    try { data = JSON.parse(scriptEl && scriptEl.textContent); } catch (_) {}
    if (!data) return;

    const modal = this._buildModal(data);
    document.body.appendChild(modal);
    document.body.classList.add("cart-recommendations-modal-open");
    requestAnimationFrame(() => modal.classList.add("is-open"));

    const close = () => this._closeModal(modal);
    [
      modal.querySelector(".cart-recommendations-modal__close"),
      modal.querySelector(".cart-recommendations-modal__backdrop"),
    ].forEach((el) => el && el.addEventListener("click", (e) => { e.stopPropagation(); close(); }));
    modal.addEventListener("click", (e) => e.stopPropagation());

    const esc = (e) => { if (e.key === "Escape") { e.stopPropagation(); close(); } };
    document.addEventListener("keydown", esc, { once: true });
    modal._escHandler = esc;
  }

  _buildModal({ title, url, featured_image, options, variants }) {
    const optionsHtml = options.map((name, idx) => {
      const key  = `option${idx + 1}`;
      const vals = [...new Set(variants.map((v) => v[key]).filter(Boolean))];
      const btns = vals.map((val) => {
        const avail = variants.some((v) => v[key] === val && v.available);
        return `<button type="button" class="cart-recommendations-opt-btn${avail ? "" : " is-unavailable"}" data-opt-index="${idx}" data-opt-value="${this._esc(val)}"${avail ? "" : ' aria-disabled="true"'}>${val}</button>`;
      }).join("");
      return `<div class="cart-recommendations-opt-group"><span class="cart-recommendations-opt-label">${name}</span><div class="cart-recommendations-opt-btns">${btns}</div></div>`;
    }).join("");

    const modal = document.createElement("div");
    modal.className = "cart-recommendations-modal";
    modal.setAttribute("role", "dialog");
    modal.setAttribute("aria-modal", "true");
    modal.setAttribute("aria-label", title);
    modal.innerHTML = `
      <div class="cart-recommendations-modal__backdrop" aria-hidden="true"></div>
      <div class="cart-recommendations-modal__dialog">
        <button type="button" class="cart-recommendations-modal__close" aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="20" height="20"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
        <div class="cart-recommendations-modal__body">
          ${featured_image ? `<img src="${featured_image}" alt="${this._esc(title)}" class="cart-recommendations-modal__image">` : ""}
          <div class="cart-recommendations-modal__content">
            <h3 class="cart-recommendations-modal__title"><a href="${url}" class="cart-recommendations-modal__title-link">${title}</a></h3>
            <div class="cart-recommendations-modal__price"></div>
            <div class="cart-recommendations-modal__options">${optionsHtml}</div>
            <button type="button" class="btn cart-recommendations-modal__atc" disabled>${this._text.addToCart}</button>
          </div>
        </div>
        <div class="cart-recommendations-modal__success" hidden>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" width="40" height="40"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11.5 14.5 15.5 10"/></svg>
          <p>${this._text.addedToCart}</p>
        </div>
      </div>`;

    modal._variants        = variants;
    modal._selectedOptions = new Array(options.length).fill(null);

    modal.querySelectorAll(".cart-recommendations-opt-btn").forEach((btn) => {
      btn.addEventListener("click", () => {
        if (btn.classList.contains("is-unavailable")) return;
        const idx = Number(btn.dataset.optIndex);
        modal.querySelectorAll(`.cart-recommendations-opt-btn[data-opt-index="${idx}"]`)
          .forEach((b) => b.classList.remove("is-selected"));
        btn.classList.add("is-selected");
        modal._selectedOptions[idx] = btn.dataset.optValue;

        const sel     = modal._selectedOptions;
        const variant = sel.some((o) => o === null)
          ? null
          : variants.find((v) => sel.every((val, i) => v[`option${i + 1}`] === val)) || null;

        const atcBtn  = modal.querySelector(".cart-recommendations-modal__atc");
        const priceEl = modal.querySelector(".cart-recommendations-modal__price");
        if (variant) {
          const onSale = variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price);
          priceEl.innerHTML = `
            ${onSale ? `<s class="cart-recommendations-card__price--compare">${this._money(variant.compare_at_price)}</s>` : ""}
            <span class="cart-recommendations-card__price--current${onSale ? " cart-recommendations-card__price--sale" : ""}">${this._money(variant.price)}</span>`;
          atcBtn.disabled     = !variant.available;
          atcBtn.textContent  = variant.available ? this._text.addToCart : this._text.soldOut;
          atcBtn.dataset.variantId = variant.id;
        } else {
          atcBtn.disabled = true;
        }
      });
    });

    modal.querySelector(".cart-recommendations-modal__atc").addEventListener("click", (e) => {
      const id = e.currentTarget.dataset.variantId;
      if (id) this._addToCart(id, e.currentTarget, modal);
    });

    return modal;
  }

  _showModalSuccess(modal) {
    modal.querySelector(".cart-recommendations-modal__body").hidden    = true;
    modal.querySelector(".cart-recommendations-modal__success").hidden = false;

    fetch("/cart.js")
      .then((r) => r.json())
      .then((cart) => {
        this._cartIds = new Set(cart.items.map((i) => i.product_id));
        document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart } }));
        setTimeout(() => {
          this._loadRecommendations(String((cart.items[0] || {}).product_id || this.dataset.productId));
          this._closeModal(modal);
        }, 1000);
      })
      .catch(() => setTimeout(() => this._closeModal(modal), 1800));
  }

  _closeModal(modal) {
    if (modal._escHandler) document.removeEventListener("keydown", modal._escHandler);
    modal.classList.remove("is-open");
    document.body.classList.remove("cart-recommendations-modal-open");
    setTimeout(() => { if (modal.parentNode) modal.remove(); }, 300);
  }

  _toCents(val) {
    if (!val) return 0;
    const n = Number(val);
    return Number.isInteger(n) && n > 100 ? n : Math.round(n * 100);
  }

  _money(cents) {
    if (window.theme && theme.Currency && theme.Currency.formatMoney && theme.settings) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    const n = (Number(cents) / 100).toFixed(2);
    return n.replace(/\B(?=(\d{3})+(?!\d))/g, ".").replace(/\.(\d{2})$/, ",$1") + " €";
  }

  _esc(str) {
    return String(str).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  }
}

if (!customElements.get("cart-recommendations")) {
  customElements.define("cart-recommendations", CartRecommendations);
}